// Generated by tutorials/record.cjs from tutorials/articles/h46-menu-swap-and-requests.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h46 --text-only` (wording) or
// `node tutorials/record.cjs h46` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'menu-swap-and-requests',
  catalogId: 'h46',
  title: 'Menu Swap and Change Requests',
  summary: 'When a delivery does not come, swap a plate, a whole meal or one item between two days without rebuilding the menu. When a facility sends back its marked-up extension sheets, read them in and apply the changes they ask for.',
  audience: 'Dietary managers and menu planners who adjust the menu from week to week',
  area: 'menus',
  learn: [
    'Swap the entree between two days for those dates only',
    'Check the menus after the swap before you save it',
    'Undo a one-time swap, or change the cycle for good',
    'Read a returned extension sheet and apply its changes',
  ],
  keywords: [
    'swap',
    'menu swap',
    'swap days',
    'switch days',
    'delivery',
    'shorted',
    'substitute',
    'one-time',
    'override',
    'change request',
    'change requests',
    'extension sheet',
    'menu request',
    'rotate',
    'move meal',
  ],
  steps: [
    {
      id: 'open',
      part: 'Swap a plate between two days',
      heading: 'Open Swap menu days',
      text: 'On the Menu Cycles page, select the cycle and then Swap. In the menu cycle editor, Swap menu days does the same.\n\nEach card is one day. Pick its date and its meal at the top of the card; the card lists what that meal serves, category by category.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/open.webp',
          width: 1280,
          height: 842,
        },
        alt: 'Swap menu days for Fall/Winter Cycle 2026: Day A and Day B, each a lunch with its categories and items, and Just these dates selected.',
      },
    },
    {
      id: 'pick',
      part: 'Swap a plate between two days',
      heading: 'Pick what moves',
      text: 'Check what moves on both days: here the ENTREE category, because tomorrow\'s entree did not come on the delivery. Each day gets the picked items from the other one, as set under Gets the picked items from.\n\nCategories that are the same every day, such as condiments, are folded away at the bottom of each card.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/pick.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/menu-swap-and-requests/pick.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.8,
        },
        alt: 'ENTREE checked on Day A and on Day B, with their entrees and the alternate checked with it.',
      },
    },
    {
      id: 'after',
      part: 'Swap a plate between two days',
      heading: 'Check the menus after the swap',
      text: 'After the swap shows each day as it will be. An arrow marks what moved in.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/after.webp',
          width: 1280,
          height: 236,
        },
        clip: {
          src: '/help/menu-swap-and-requests/after.mp4',
          width: 1280,
          height: 236,
          durationSec: 2.7,
        },
        alt: 'After the swap: Day A now has Day B\'s entrees and Day B has Day A\'s, each marked with an arrow.',
      },
    },
    {
      id: 'swap',
      part: 'Swap a plate between two days',
      heading: 'Swap',
      text: 'Type why if you like; it is saved with the change. Then select Swap and confirm.\n\nWith Just these dates, each date gets its own one-time menu. Your menu cycle stays as it is.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/swap.webp',
          width: 708,
          height: 366,
        },
        clip: {
          src: '/help/menu-swap-and-requests/swap.mp4',
          width: 708,
          height: 366,
          durationSec: 2,
        },
        alt: 'The confirmation asking to swap the two lunches, saying one-time menus are made and the cycle is not changed.',
      },
    },
    {
      id: 'done',
      part: 'Swap a plate between two days',
      heading: 'Undo, or open the new menus',
      text: 'Done lists the one-time menus that were made, one for each date. Select one to open it and change it further.\n\nSelect Undo to put everything back. Undo is there until you leave the page.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/done.webp',
          width: 1280,
          height: 184,
        },
        clip: {
          src: '/help/menu-swap-and-requests/done.mp4',
          width: 1280,
          height: 184,
          durationSec: 2,
        },
        alt: 'Done: 2 one-time menus made, listed as Menu Override with their dates, with Undo, Swap something else and Back to the menu cycle.',
      },
    },
    {
      id: 'cycle',
      part: 'Swap a plate between two days',
      heading: 'Change the cycle for good',
      text: 'When the change is permanent, select Change the cycle at the top. The cards then pick cycle days instead of dates, such as Day 9 (Week 2, Day 2), and the swap moves the cycle\'s own rows for every week it runs.\n\nAn item checked on its own needs a category of the same kind on the day it goes to. When that day has none, a red note next to the item says so, and Swap stays off until you check the whole category instead.\n\nYou check a box to say you understand before Swap works. There is no undo.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/cycle.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/menu-swap-and-requests/cycle.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'Change the cycle selected: the cards show cycle day pickers with the week and day under each, and the note that the cycle days change every time they come round.',
      },
    },
    {
      id: 'requests',
      part: 'Apply a change request',
      heading: 'Open Change Requests',
      text: 'When a facility marks up its printed extension sheets and sends them back, open Menus, then Change Requests. Drop the returned sheets on the box, or select Choose files. Pick several at once when they belong to the same week.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/requests.webp',
          width: 1280,
          height: 842,
        },
        alt: 'Change Requests with the box for the returned extension sheets.',
      },
    },
    {
      id: 'read',
      part: 'Apply a change request',
      heading: 'See what the sheet asks for',
      text: 'The sheet is read against the menu it was printed from. Each meal lists its changes: entrees that move between days, rows to remove, items to add, and diet cells changed.\n\nAn item the cycle already serves on another day can be copied with its diet cells; leave it checked. Diet cell changes are listed for you to make in Modify.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/read.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/menu-swap-and-requests/read.mp4',
          width: 1280,
          height: 842,
          durationSec: 2,
        },
        alt: 'The sheet read against Fall/Winter Cycle 2026: Monday Lunch takes Tuesday\'s entree and removes a dessert, and Tuesday Lunch takes Monday\'s entree, adds an item and changes a Renal cell.',
      },
    },
    {
      id: 'apply',
      part: 'Apply a change request',
      heading: 'Apply the changes',
      text: 'Keep Just those dates to make one-time menus for the dates on the sheet, or pick The cycle when the facility marked a permanent change. Then select Apply.\n\nThe line beside Apply says how many changes it makes and how many are left to key in Modify.',
      media: {
        image: {
          src: '/help/menu-swap-and-requests/apply.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/menu-swap-and-requests/apply.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.6,
        },
        alt: 'Done: 2 one-time menus made from the change request, one for each date, with Undo.',
      },
    },
  ],
  tips: [
    'Main plate checks every category that differs between the days (entree, sides, bread, dessert) in one go. Whole meal checks everything.',
    'Add a day to rotate three or four days: each day takes the picked items from the day before it.',
    'Check single items instead of a whole category to move just one dish, such as the soup.',
    'Open a one-time menu from the list under Done to change it further, like any holiday menu.',
  ],
  notes: [
    'Just these dates makes a one-time menu for each date, starting from what that date serves today. The cycle is not touched, and Undo puts everything back while you are still on the page.',
    'Change the cycle moves the cycle\'s own rows, for every week the cycle runs. There is no undo, and residents\' selective menu choices for those days are not moved.',
    'Swapping and change requests need the menu cycle override rights. If you do not see Swap or Change Requests, ask your administrator.',
  ],
  related: ['menu-cycles', 'reading-a-menu-cycle', 'changing-menu-items', 'holiday-and-one-time-menus'],
  recorded: '2026-10-26',
};

export default article;
